/**
 * Copier — un bouton qui copie une valeur (un lien d'invitation, une adresse,
 * un identifiant) et le dit : « Copié », une coche, une seconde et demie.
 *
 *   <BoutonCopier valeur={lien.url} />                                   ← « Copier », puis « Copié »
 *   <BoutonCopier valeur={id} iconeSeule texte="Copier l'identifiant" />  ← dans une cellule
 *   <ChampCopiable etiquette="Ton lien" valeur="https://t.me/trista_bot" aide="Colle-le dans ta bio." />
 *
 * `copierTexte` passe par le presse-papiers du navigateur et, quand il n'y en a
 * pas (une page servie en http, une vieille webview), par une sélection ; il rend
 * `true` si la copie a pris. La confirmation est annoncée à un lecteur d'écran
 * (`aria-live`). Le champ copiable se lit en entier : un clic dedans sélectionne
 * tout, la valeur défile si elle est longue, jamais tronquée par des « … ».
 *
 * Successeur des 30 `navigator.clipboard.writeText(…).then(() => setCopie(true);
 * setTimeout(…, 1500))` du cockpit et d'Helios : `MonLienTab`, `MesLiens`,
 * `CleApi`, `Parrainage`, `TreasuryZone`, `WalletTab`, `InviteLinksView`,
 * `ConversationIdPill`, les cartes d'Helios…
 */
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { Button, type ButtonSize, type ButtonVariant } from "../Button";
import { SousLeChamp, TAILLES_CHAMP, type TailleChamp } from "../Champ";
import { Check, Copy } from "../icons";
import { cn } from "../utils";
import "../champs.css";
import "./saisie.css";

/** Copie un texte. `true` si c'est fait. */
export async function copierTexte(texte: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texte);
      return true;
    }
  } catch {
    // le presse-papiers refuse (page non sécurisée, permission) : la sélection prend le relais
  }
  try {
    const zone = document.createElement("textarea");
    zone.value = texte;
    zone.setAttribute("readonly", "");
    zone.style.position = "fixed";
    zone.style.opacity = "0";
    document.body.appendChild(zone);
    zone.select();
    const ok = typeof document.execCommand === "function" && document.execCommand("copy");
    zone.remove();
    return ok;
  } catch {
    return false;
  }
}

/** L'état « copié » d'un bouton : `copier(texte)` le pose pour `duree` ms. */
export function useCopie(duree = 1500): { copie: boolean; copier: (texte: string) => Promise<boolean> } {
  const [copie, setCopie] = useState(false);
  const minuterie = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(minuterie.current), []);
  async function copier(texte: string) {
    const ok = await copierTexte(texte);
    if (ok) {
      setCopie(true);
      window.clearTimeout(minuterie.current);
      minuterie.current = window.setTimeout(() => setCopie(false), duree);
    }
    return ok;
  }
  return { copie, copier };
}

export interface BoutonCopierProps {
  /** Ce qu'on copie. */
  valeur: string;
  /** Le texte du bouton — déjà traduit (défaut « Copier »). Avec `iconeSeule`, c'est son nom pour un lecteur d'écran. */
  texte?: string;
  /** Après la copie (défaut « Copié »). */
  texteCopie?: string;
  /** L'icône seule, sans le mot (une cellule, un champ). */
  iconeSeule?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Combien de temps « Copié » reste (défaut 1 500 ms). */
  duree?: number;
  /** Après une copie réussie (un toast, un compteur). */
  onCopie?: (valeur: string) => void;
  disabled?: boolean;
  className?: string;
}

export function BoutonCopier({
  valeur,
  texte = "Copier",
  texteCopie = "Copié",
  iconeSeule = false,
  variant = "secondary",
  size = "md",
  duree = 1500,
  onCopie,
  disabled,
  className,
}: BoutonCopierProps) {
  const { copie, copier } = useCopie(duree);
  const icone = copie ? <Check weight="bold" className="h-[1.15em] w-[1.15em]" /> : <Copy className="h-[1.15em] w-[1.15em]" />;
  return (
    <>
      <Button
        variant={variant}
        size={size}
        iconOnly={iconeSeule}
        icon={iconeSeule ? undefined : icone}
        aria-label={iconeSeule ? (copie ? texteCopie : texte) : undefined}
        title={iconeSeule ? (copie ? texteCopie : texte) : undefined}
        disabled={disabled || valeur === ""}
        data-copie={copie || undefined}
        className={cn(copie && "si-copie-faite", className)}
        onClick={() => {
          void copier(valeur).then((ok) => {
            if (ok) onCopie?.(valeur);
          });
        }}
      >
        {iconeSeule ? icone : copie ? texteCopie : texte}
      </Button>
      <span className="sr-only" role="status" aria-live="polite">
        {copie ? texteCopie : ""}
      </span>
    </>
  );
}

export interface ChampCopiableProps {
  valeur: string;
  etiquette?: ReactNode;
  aide?: ReactNode;
  /** Une icône à gauche (un lien, une clé, un logo). */
  icone?: ReactNode;
  /** La police à chasse fixe (une adresse, une clé). Défaut : vrai si la valeur n'a pas d'espace. */
  mono?: boolean;
  /** Les textes du bouton — déjà traduits. */
  texte?: string;
  texteCopie?: string;
  /** Le bouton en icône seule, au bout du champ (défaut : le mot sur un écran large). */
  iconeSeule?: boolean;
  /** Quand la valeur manque : ce texte, en gris, et rien à copier. */
  vide?: string;
  onCopie?: (valeur: string) => void;
  taille?: TailleChamp;
  id?: string;
  className?: string;
}

/** Une valeur en lecture, dans la surface d'un champ, avec son bouton Copier au bout. */
export function ChampCopiable({
  valeur,
  etiquette,
  aide,
  icone,
  mono,
  texte = "Copier",
  texteCopie = "Copié",
  iconeSeule = false,
  vide,
  onCopie,
  taille = "md",
  id: idDonne,
  className,
}: ChampCopiableProps) {
  const genere = useId();
  const id = idDonne ?? genere;
  const idAide = `${id}-aide`;
  const t = TAILLES_CHAMP[taille];
  const chasseFixe = mono ?? !/\s/.test(valeur);
  const manque = valeur === "";
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {etiquette !== undefined && (
        <label htmlFor={id} className="ch-etiquette">
          {etiquette}
        </label>
      )}
      <div className={cn("ch-surface flex items-center", t.arrondi, t.cadre, manque ? cn(t.lignes, "min-h-10") : t.hauteur, "pr-1")}>
        {icone !== undefined && <span className="ch-icone shrink-0 text-faint">{icone}</span>}
        {manque ? (
          // Rien à copier : la raison, en entier (elle va à la ligne), pas un champ qui la coupe.
          <output id={id} aria-describedby={aide !== undefined ? idAide : undefined} className="min-w-0 flex-1 leading-snug text-faint">
            {vide ?? ""}
          </output>
        ) : (
          <input
            id={id}
            readOnly
            value={valeur}
            aria-describedby={aide !== undefined ? idAide : undefined}
            className={cn("ch-saisie", chasseFixe && "font-mono text-[0.92em]")}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.currentTarget.select()}
          />
        )}
        {!manque && (
          <BoutonCopier
            valeur={valeur}
            texte={texte}
            texteCopie={texteCopie}
            iconeSeule={iconeSeule}
            variant="ghost"
            size={taille === "lg" ? "lg" : taille === "sm" ? "sm" : "md"}
            onCopie={onCopie}
            className="shrink-0"
          />
        )}
      </div>
      <SousLeChamp id={idAide} aide={aide} />
    </div>
  );
}
